<template>
  <div v-if="editorialContent">
    <ux-molecules-editorial-hero
      :alt="editorialContent?.coverImage?.alt"
      :heading="hero.title"
      :label="hero.label"
      :media="editorialContent?.coverImage"
      :src="editorialContent?.coverImage?.src"
      :subheading="hero.subheading"
    />
    <section class="Grid-layout">
      <div class="Grid-twelve Grid-twelve--subgrid Grid-twelve--editorial">
        <div>
          <template :key="`cms-richtext-${index}`" v-for="(component, index) in components">
            <component v-bind="component" :is="getComponent(component.nodeType)" v-if="component.elements" />
          </template>
        </div>
        <ux-molecules-editorial-downloads
          :pdf-href="editorialContent?.pdfUrl"
          :terms-and-conditions-description="editorialContent?.termsAndConditionsDescription"
          :terms-and-conditions-title="editorialContent?.termsAndConditionsTitle"
          v-if="editorialContent.pdfUrl || (editorialContent?.termsAndConditionsDescription && editorialContent?.termsAndConditionsTitle)"
        />
      </div>
    </section>
    <section class="Layout" v-if="selectedLoyaltyBanner?.title">
      <ux-molecules-loyalty-banner
        :button-label="!userStore.isLogged ? $t('ux.molecules.navAccountDropdown.login') : selectedLoyaltyBanner?.ctaLabel"
        :button-link="!userStore.isLogged ? loginLink : selectedLoyaltyBanner?.ctaUrl"
        :external="userStore.isLogged"
        :secondary-button-label="!userStore.isLogged ? selectedLoyaltyBanner?.ctaLabel : null"
        :secondary-button-link="!userStore.isLogged ? selectedLoyaltyBanner?.ctaUrl : null"
        :subtitle="selectedLoyaltyBanner?.subtitle"
        :title="selectedLoyaltyBanner.title"
        logo="logo-all"
      />
    </section>
    <section class="Grid-layout" v-if="restaurantCities?.length">
      <ux-molecules-inspiration-filter
        :filters="restaurantCities"
        :filters-title="$t('ux.molecules.editorialContent.restaurantFilter.filterByCity')"
        :label="$t('ux.molecules.editorialContent.restaurantFilter.title')"
        @inspiration-filter::filter-update="filterUpdate"
      />
    </section>
    <template v-if="restaurants?.length">
      <section>
        <ux-molecules-grid-card-restaurant :cards="restaurants" :columns="3" :page-name="gtmCardRestaurantName" />
      </section>
      <ux-molecules-map
        :markers="mapMarkersList"
        :variant="MapVariant.INSPIRATION"
        @map::selected-restaurant="scrollToRestaurant"
        class="Grid-layout__item--fullWidth"
      />
    </template>
    <section class="Layout" v-else>
      <ux-atoms-typo as="p" color="dark">{{ $t('pages.edito.noRestaurant') }}</ux-atoms-typo>
    </section>
    <ux-molecules-inspiration-insert :cards="linkedEditorialContents" v-if="linkedEditorialContents.length > 0" />
  </div>
</template>
<script lang="ts" setup>
import CmsRichTextList from '~/components/ux/atoms/RichText/CmsRichTextList/CmsRichTextList.vue';
import CmsRichTextText from '~/components/ux/atoms/RichText/CmsRichTextText/CmsRichTextText.vue';
import { RichTextComponentType } from '~/components/ux/atoms/RichText/types';
import { VariantType, cardListType, cardType } from '~/components/ux/molecules/CardRestaurant/types';
import { FiltersContent } from '~/components/ux/molecules/InspirationFilterContent/InspirationFilterContent.vue';
import { Lang } from '~/components/ux/molecules/LangDropdown/types';
import { MapVariant, markerType } from '~/components/ux/molecules/Map/types';
import { LoyaltyProgram, LoyaltyType, LoyaltyUserStatus } from '~/core';
import { EDITORIAL_TO_REDIRECT, OLD_ARTICLE_TO_REDIRECT_TO_HP } from '~/core/constants';
import {
  EditorialContent,
  EditorialContentQueryVariables,
  useEditorialContentAsyncData,
  useEditorialContentLazyQuery,
  useLoyaltyBannersAsyncData
} from '~/graphql';
import { capitalizeFirstLetter } from '~/helpers/capitaliseFirstLetter';
import { gtmPageAndUser } from '~/helpers/gtm';
import { useScrollToContainer } from '~/helpers/scrollToContainer';

const route = useRoute();
const router = useRouter();
const setI18nParams = useSetI18nParams();
const { locale, locales, t } = useI18n();
const localePath = useLocalePath();
const userStore = useUserStore();
const { getAuthUrl } = useAuth();

const type = computed(() => (route.name ? (route.name as string).split('-')[0] : ''));
const slug = computed(() => (route.params.slug ? (route.params.slug as string) : ''));
const filteredCities = computed(() => route.query.filteredCities ?? []);

const gtmCardRestaurantName = computed(() => `restaurantsandbars::offer::${editorialContent.value?.title}`);

const variables = computed(() => ({
  filteredCities: filteredCities.value,
  slug: slug.value,
  type: type.value
}));

const { data: LoyaltyBannerData } = await useLoyaltyBannersAsyncData({
  type: LoyaltyType.Home
});

const loginLink = computed(() => {
  return getAuthUrl('login');
});

const loyaltyBanners = computed(() => LoyaltyBannerData.value?.loyaltyBanner);

const selectedLoyaltyBanner = computed(() =>
  loyaltyBanners.value?.find(
    (loyaltyBanner) =>
      (userStore.isLogged ? loyaltyBanner?.userStatus === LoyaltyUserStatus.LOGGED_IN : loyaltyBanner?.userStatus === LoyaltyUserStatus.LOGGED_OUT) &&
      loyaltyBanner?.type === LoyaltyType.Editorial &&
      loyaltyBanner?.program === LoyaltyProgram.All
  )
);

const { data } = await useEditorialContentAsyncData({ ...variables.value });

const { load, onResult } = useEditorialContentLazyQuery({ ...variables.value });

const linkedEditorialContents = computed(() => data.value?.editorialContent?.LinkedEditorialContent as EditorialContent[]);

const editorialContent = computed(() => data.value?.editorialContent ?? null);

if (route?.name?.toString().includes('article') && OLD_ARTICLE_TO_REDIRECT_TO_HP.includes(route.path)) {
  await navigateTo({ hash: '#info-modal', path: localePath('/' + t('pages.homePage.defaultCountry')) }, { redirectCode: 301 });
} else if (!route?.name?.toString().includes('article') && EDITORIAL_TO_REDIRECT.includes(route.path) && !editorialContent.value) {
  await navigateTo({ path: localePath('/') }, { external: true, redirectCode: 302 });
} else {
  useRedirectIfEmpty(editorialContent.value);
}

const restaurants = computed<cardListType>(
  () =>
    (editorialContent.value?.restaurants ?? []).map((restaurant) => ({
      address: `${restaurant?.city} ${restaurant?.zipCode}`,
      alt: t('ux.molecules.sliderCardRestaurant.altCardImage', { restaurant: restaurant?.name }),
      availabilities: restaurant?.availabilities,
      averageRating: restaurant?.averageRating,
      bookingWidgetUrl: restaurant?.bookingWidgetUrl,
      currency: restaurant?.currency,
      enrolmentPrograms: restaurant?.enrolmentPrograms,
      facetCodes: restaurant?.facetCodes,
      foodType: restaurant?.foodType,
      hotelCode: restaurant?.hotelCode as string,
      href: localePath({ name: 'restaurant-restaurantCode', params: { restaurantCode: restaurant?.id } }),
      id: restaurant?.id,
      identifiers: restaurant?.identifiers,
      lat: restaurant?.lat,
      lon: restaurant?.lon,
      medias: restaurant?.medias,
      partnerId: restaurant?.partnerId,
      phone: restaurant?.phonePrefix && restaurant.phone ? `+${restaurant?.phonePrefix}${restaurant?.phone}` : undefined,
      price: restaurant?.averagePrice,
      reviewCount: restaurant?.reviewCount,
      src: restaurant?.imageUrl,
      stars: restaurant?.star,
      title: restaurant?.name,
      variant: VariantType.List
    })) as unknown as cardListType
);

const restaurantCities = computed(() =>
  (editorialContent.value?.restaurantsCities ?? []).map((el) => ({
    id: el?.slug,
    label: capitalizeFirstLetter(el?.name),
    value: filteredCities.value.includes(el?.slug as string)
  }))
);

const filterUpdate = async (filtersUpdated: FiltersContent[]) => {
  const cities = filtersUpdated
    .filter((filter) => filter.value)
    .map((filter) => filter.id)
    .filter((id): id is string => !!id);

  await handleUpdateEditorialContent({ ...variables.value, filteredCities: cities });
};

const handleUpdateEditorialContent = async (filtersUpdated: EditorialContentQueryVariables) => {
  onResult((response) => {
    if (response.loading) return;

    if (response.data.editorialContent) {
      data.value = response.data;
      router.push({
        query: {
          filteredCities: filtersUpdated.filteredCities
        }
      });
    }
  });

  await load(undefined, { ...filtersUpdated });
};

const i18nParams = computed(() => {
  const currentLocale = locale.value as Lang;
  const currentSlug = editorialContent.value?.slugs?.[currentLocale];

  const params = {
    [currentLocale]: {
      slug: currentSlug
    }
  };

  for (const el of locales.value) {
    if (el.code !== currentLocale) {
      params[el.code] = {
        slug: editorialContent.value?.slugs?.[el.code as Lang]
      };
    }
  }

  return params;
});

const hero = computed(() => ({
  label: t(`pages.edito.${editorialContent.value?.variant?.toLocaleLowerCase()}`) ?? '',
  subheading: editorialContent.value?.subtitle,
  title: editorialContent.value?.title
}));

const components = computed(() => {
  if (!editorialContent.value || !editorialContent.value.content) {
    return [];
  }

  return usePersonalizedPage(editorialContent.value.content);
});

const clusters = computed(() =>
  Object.values(
    (restaurants.value || []).reduce(
      (acc, obj, index) => {
        const key = obj.hotelCode ? obj.hotelCode : `_noHotelCode_${index}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(obj as unknown as cardType);
        return acc;
      },
      {} as Record<string, cardType[]>
    )
  )
);

const mapMarkersList = computed(() =>
  clusters.value?.map(
    (i) =>
      ({
        count: i.length,
        id: i[0].id,
        name: i.map((item) => item.title),
        position: { lat: i[0].lat, lng: i[0].lon }
      }) as markerType
  )
);

const scrollToRestaurant = (restaurantId: string) => {
  const restaurant = restaurants.value.find((el) => el.id === restaurantId);

  if (restaurant) {
    useScrollToContainer(`restaurant-card_${restaurantId}`, undefined, 'start', 50);
  }
};

watch(
  () => i18nParams.value,
  () => {
    setI18nParams(i18nParams.value);
  },
  {
    immediate: true
  }
);

const getComponent = (nodeType: RichTextComponentType) => {
  switch (nodeType) {
    case RichTextComponentType.Text:
      return CmsRichTextText;
    case RichTextComponentType.List:
      return CmsRichTextList;
    default:
      return null;
  }
};

const pageEvent = () => {
  gtmPageAndUser({
    brandcontext: 'restaurantsandbars',
    category: 'restaurantsandbars',
    countrymarket: 'fr',
    currencycode: 'EUR',
    dynamic_datas_ready: true,
    pagename: `restaurantsandbars::${editorialContent.value?.variant?.toLocaleLowerCase()}::${editorialContent.value?.title}`,
    pagetype: 'restaurantsandbars',
    sub_category_level1: editorialContent.value?.variant?.toLocaleLowerCase(),
    sub_category_level2: editorialContent.value?.title ?? ''
  });
};

whenever(
  () => userStore.userLoadDone && editorialContent.value?.variant,
  () => {
    pageEvent();
  }
);

onMounted(() => {
  if (userStore.userLoadDone && editorialContent.value?.variant) {
    pageEvent();
  }
});

useHead(() => ({
  meta: [
    {
      content: editorialContent.value?.metaDescription ?? t('pages.editorialContent.metaDescription', { name: editorialContent.value?.title }),
      name: 'description'
    }
  ],
  title: editorialContent.value?.metaTitle ?? t('pages.editorialContent.metaTitle', { name: editorialContent.value?.title })
}));
</script>
